import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';

import {
  DrilldownFormsService,
  DynamicFormsCompleteEvent,
  DynamicFormsEvent,
  PccCrash,
  TabsEnum,
} from '../../services/drilldown-forms.service';
import { CommonService } from '../../services/common.service';
import { ReportsService, ReportMeta } from '../../services/reports.service';
import { PromptWithActionDialogComponent } from '../prompt-with-action/prompt-with-action.component';

import { SidepaneItem } from '../../model/dialogs';
import { OtherMiniReportOptions } from 'src/app/model/reports';

interface DialogData {
  selectedRow: any;
  securePin?: boolean;
  hidePin?: boolean;
  reportEndpointName: string;
  autoExpandCoachForm: boolean;
  autoExpandOneToOneForm: boolean;
  autoExpandPccForm: boolean;
  autoExpandDerForm: boolean;
  autoExpandVirtualSafetyDiscussionForm: boolean;
  defaultTab: any;
  targetIntervention?: string;
}

interface Intervention {
  name: string;
  value: string;
  allowUserCreation?: boolean;
}

interface VehicleTypeTabCheck {
  car?: boolean;
  flt?: boolean;
  '2w'?: boolean;
}

@Component({
  selector: 'driver-index-dialog',
  templateUrl: './driver-index.component.html',
  styleUrls: ['./driver-index.component.scss'],
})
export class DriverIndexComponent implements OnInit {
  public shouldHidePin = this.data.hidePin ?? false;
  protected displayFsdsChart = this.cService.userProfile.companyOptionalSettings.displayFSDS;
  roadRiskWithRegTrademark: string = 'RoadRISK<sup>&reg;</sup>';
  maxYearsWithoutRoadRiskAssessment: number = 3;
  lastRoadRiskAssessmentInYears = { car: null, flt: null, '2w': null };
  roadRiskAssessmentOverdue: VehicleTypeTabCheck = { car: false, flt: false, '2w': false };

  firstLastNames: string;
  driverIdentifier: string;
  showDetailsBufferProgress: boolean = false;
  sidepaneItems: Array<SidepaneItem> = [];
  reportEndpointName: string;

  applicableCoachingInterventions: Array<Intervention> = [];
  pccReviewCrashes: Array<PccCrash> = [];
  canCreateAnIntervention: boolean = false;
  hasSingleIntervention: boolean = false;
  singleInterventionButtonText: string;
  singleIntervention: Intervention;
  hasPendingSingleIntervention: boolean = false;
  isPccReview: boolean = false;

  coachingHistoryMeta: ReportMeta;
  coachingHistory: Array<any>;
  reportOptions: OtherMiniReportOptions = {
    reportHeight: '100%',
  };

  reviewPeriods: Array<any> = [];
  outstandingReviews: number;
  outstandingPccReviews: number;
  outstandingOneToOnePlusReviews: number;
  selectedCoachFormData: any;
  selectedPeriod: any;
  coachFormIsReadonly: boolean = false;
  dialogDataReady: boolean = false;
  insightsDataReady: VehicleTypeTabCheck = {
    car: false,
    flt: false,
    '2w': false,
  };
  insightsDataFailed: VehicleTypeTabCheck = {
    car: false,
    flt: false,
    '2w': false,
  };
  renderMultipleCharts: boolean = false;

  driverIndexSummaryChart: any;
  mentorCharts: Array<any> = [];
  canCreateAdHocIntervention: boolean;
  summaryTabHaveContent: boolean;
  canViewCoachingToolkitTab: boolean;
  canViewMentorRewardsSummary: boolean;

  roadRisk = { car: null, flt: null, '2w': null };
  roadRiskFullInfo = { car: [], flt: [], '2w': [] };
  roadRiskDriverVehicleTypes: Array<string> = [];
  riskRatings = {
    low: 'low',
    medium: 'medium',
    high: 'high',
    veryHigh: 'very high',
  };
  riskRatingColors = {
    'n/a': 'grey',
    low: 'green',
    medium: 'orange',
    high: 'red',
    'very high': 'red',
  };
  riskComparisonValues = {
    worse: 'worse',
    better: 'better',
    same: 'same',
  };

  constructor(
    private dialogRef: MatDialogRef<DriverIndexComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialog: MatDialog,
    public translate: TranslateService,
    public dfService: DrilldownFormsService,
    public cService: CommonService,
    public rptService: ReportsService
  ) {}

  ngOnInit() {
    console.log('DIALOG DATA', this.data);
    this.dfService.selectedDriver = this.data.selectedRow;

    // Used when an Intervention is opened in a new Window / Tab
    localStorage.setItem(this.cService.localStorageKeys.selectedDriver, JSON.stringify(this.dfService.selectedDriver));

    this.initialize();
  }

  initialize() {
    this.rptService.formDataUpdated = false;

    this.showDetailsBufferProgress = true;
    this.reportEndpointName = this.data.reportEndpointName;
    this.dfService.currentTabIndex = this.data.defaultTab
      ? this.data.defaultTab
      : this.dfService.drilldownTabs.insights.id;

    this.canCreateAdHocIntervention =
      this.cService.userProfile.companyPermissions.permMisAdHocTraining == this.cService.permLevels.TRUE ? true : false;

    this.summaryTabHaveContent = this.summaryTabWillHaveContent();

    this.canViewCoachingToolkitTab =
      this.cService.userProfile.companyPermissions.permMisManagerCoaching == this.cService.permLevels.TRUE
        ? true
        : false;

    this.canViewMentorRewardsSummary =
      this.cService.userProfile.companyPermissions.permMisDriverRecordMentorRewards === this.cService.permLevels.TRUE &&
      this.cService.userProfile.userPermissions.permDriverRecordMentorRewards === this.cService.permLevels.TRUE;

    this.updateFirstLastNames();
    this.driverIdentifier = this.dfService.getSelectedDriverIdentifier(this.shouldHidePin);

    this.collateSidepaneItems(null, this.data.securePin);

    this.fetchData(this.dfService.selectedDriver.pin, this.data.securePin);

    this.dialogRef.beforeClosed().subscribe({
      next: () => {
        this.rptService.triggerDriverPerformanceDialogClosedObservable(this.rptService.endpointFilterValue, true);
        this.rptService.formDataUpdated = false;

        this.dfService.closeFormPane();

        // Remove cached selected driver, used for new-tab preview in resource-preview comp
        localStorage.removeItem(this.cService.localStorageKeys.selectedDriver);
        localStorage.removeItem(this.cService.localStorageKeys.selectedCasualReportRow);
      },
    });
  }

  summaryTabWillHaveContent(): boolean {
    return (
      this.cService.userProfile.userPermissions?.permIndexing === this.cService.permLevels.view ||
      this.cService.userProfile.companyPermissions?.permIsMentor === 1
    );
  }

  // EVENTS . METHODS
  selectedTabChanged(selectedTab: { index: number }) {
    if (selectedTab.index == TabsEnum.history) {
      this.getCoachingHistoryTabData(this.data.securePin);
    }
  }

  newItemAddedToPanel(newItem: any) {
    this.collateSidepaneItems(newItem, this.data.securePin);
  }

  updateFirstLastNames() {
    this.firstLastNames = this.dfService.selectedDriver.firstName + ' ' + this.dfService.selectedDriver.lastName;
  }

  // METHODS
  private getAndInitializeRoadRiskData(securePin = false) {
    this.initializeInsightsDataFlagStates();

    this.dfService.getDriverRoadRiskVehicleTypes(this.dfService.selectedDriver.pin, securePin).subscribe({
      next: (response: Array<string>) => {
        this.roadRiskDriverVehicleTypes = response;
        this.loadRoadRiskData();
      },

      error: () => {
        this.cService.showToast(`:: ${this.cService.readyTranslations.messagesAndWarnings.failedToFetchData}`);
      },
    });
  }

  private loadRoadRiskData() {
    this.roadRiskDriverVehicleTypes.forEach((vehicleType: string) => {
      const availableInTabs = this.cService.tabVehicleTypes[vehicleType];

      if (vehicleType && availableInTabs) {
        this.dfService.getRoadRiskTabData(this.dfService.selectedDriver.pin, vehicleType).subscribe({
          next: (response: { roadRisk: any; roadRiskFullInfo: Array<any> }) => {
            this.roadRisk[vehicleType] = response.roadRisk;
            this.roadRiskFullInfo[vehicleType] = response.roadRiskFullInfo;
            this.setInsightsDataReadyTrue(vehicleType);

            this.calculateYearsSinceLastRoadRiskAssessment(vehicleType);
          },

          error: () => {
            this.insightsDataFailed[vehicleType] = true;
          },
        });
      }
    });
  }

  private initializeInsightsDataFlagStates() {
    for (const tab in this.insightsDataReady) {
      if (tab) {
        this.insightsDataReady[tab] = false;
        this.insightsDataFailed[tab] = false;
      }
    }
  }

  private setInsightsDataReadyTrue(vehicleTypeOrTab: string) {
    setTimeout(() => {
      this.insightsDataReady[vehicleTypeOrTab] = true;
    }, 250);
  }

  private calculateYearsSinceLastRoadRiskAssessment(vehicleType: string) {
    if (this.roadRisk[vehicleType]) {
      this.roadRiskAssessmentOverdue[vehicleType] = false;

      const momentDateInput = moment.utc(this.roadRisk[vehicleType].completionDate, 'YYYY-MM-DD');
      const yearsSince = moment().diff(momentDateInput, 'years', true).toFixed();

      if (yearsSince) {
        this.lastRoadRiskAssessmentInYears[vehicleType] = Number(yearsSince);
      }

      if (
        this.lastRoadRiskAssessmentInYears[vehicleType] &&
        this.lastRoadRiskAssessmentInYears[vehicleType] > this.maxYearsWithoutRoadRiskAssessment
      ) {
        this.roadRiskAssessmentOverdue[vehicleType] = true;
      } else {
        // Override by server verdict
        if (this.roadRisk[vehicleType].assessmentCompleted === false) {
          this.roadRiskAssessmentOverdue[vehicleType] = true;
        }
      }
    }

    console.log(
      `%c Last Assessment Year ${vehicleType}`,
      'color: limegreen',
      this.lastRoadRiskAssessmentInYears[vehicleType]
    );
  }

  sendGuideDocument(documentId: string) {
    this.dfService.sendRoadRiskGuideDocument(documentId);
  }

  private pushSidepaneItem(sidepaneItem: any) {
    const driverIdentifier: string = this.dfService.getDriverIdentifier(
      sidepaneItem.pin,
      sidepaneItem?.cdbId,
      sidepaneItem?.realStaffNumber,
      this.shouldHidePin
    );

    this.sidepaneItems.unshift({
      title: `${sidepaneItem.firstName + ' ' + sidepaneItem.lastName}`,
      pin: sidepaneItem.pin,
      description: driverIdentifier,
      data: sidepaneItem,
    });
  }

  private collateSidepaneItems(newItem?: any, securePin = false) {
    /**
     * NOTE: The vrm-mentor-reports component already ensured that
     * selectedRow defaults to the first array item in case of multiple edit.
     */
    if (this.rptService.selectedRows.length > 0) {
      // Collate sidepane items
      if (newItem) {
        const alreadySelectedInGrid = this.rptService.selectedRows.find((item) => item.pin === newItem.pin);
        if (!alreadySelectedInGrid) {
          this.rptService.addNewlySelectedRowAndTriggerObservable(newItem);
        }

        this.dfService.fetchOutstandingInterventionsPerDriver([newItem.pin], securePin).subscribe({
          next: (response) => {
            newItem.outstanding = response.data[0].outstanding;
            this.pushSidepaneItem(newItem);
          },

          error: () => {
            this.pushSidepaneItem(newItem);
            this.cService.showToast(`:: ${this.cService.readyTranslations.messagesAndWarnings.failedToFetchData}`);
          },
        });
      } else {
        const batchPins: Array<any> = this.rptService.selectedRows.map((row) => row.pin);

        this.dfService.fetchOutstandingInterventionsPerDriver(batchPins, securePin).subscribe({
          next: (response: { data: Array<any> }) => {
            this.rptService.selectedRows.forEach((selectedData) => {
              selectedData.outstanding = response.data.find((item) => item.pin == selectedData.pin).outstanding;
              this.pushSidepaneItem(selectedData);
            });
          },

          error: () => {
            this.rptService.selectedRows.forEach((selectedData) => {
              this.pushSidepaneItem(selectedData);
            });
            this.cService.showToast(`:: ${this.cService.readyTranslations.messagesAndWarnings.failedToFetchData}`);
          },
        });
      }
    }
  }

  private updateOutstandingInterventionCount(securePin = false) {
    const pin: any = this.dfService.selectedDriver.pin;

    this.dfService.fetchOutstandingInterventionsPerDriver([pin], securePin).subscribe({
      next: (response) => {
        if (response && Array.isArray(this.sidepaneItems) && this.sidepaneItems.length) {
          if (response.data && response.data.length) {
            this.sidepaneItems.find((item) => item.pin == pin).data.outstanding = response.data[0].outstanding;
          }
        }
      },
    });
  }

  paneItemSelected(event: { pin: any; data: any }) {
    const selectedRowPin = this.dfService.selectedDriver ? this.dfService.selectedDriver.pin : null;

    if (event && event.pin) {
      if (event.pin !== selectedRowPin) {
        this.dfService.closeFormPane();

        this.dfService.selectedDriver = this.rptService.selectedRows.find((itemData) => itemData.pin == event.pin);
        this.updateFirstLastNames();
        this.driverIdentifier = this.dfService.getSelectedDriverIdentifier(this.shouldHidePin);

        this.fetchData(event.pin);
      }
    } else {
      console.error('VRM ERROR: pin or event.pin not defined, hence no request made to the API');
    }
  }

  private openProvisionedFormByDefault() {
    if (this.data.autoExpandCoachForm) {
      if (this.coachingHistory.length > 0) {
        const interventionToOpen = this.coachingHistory.find(
          (intervention) =>
            intervention.status.toLowerCase().includes('required') &&
            intervention.interventionValue === this.dfService.driverImprovementInterventionValue
        );

        if (interventionToOpen) {
          this.dfService.launchSelectedIntervention(interventionToOpen);
        }
      }

      this.data.autoExpandCoachForm = false;
    }

    if (this.data.autoExpandOneToOneForm) {
      if (this.coachingHistory.length > 0) {
        const statusToOpen = ['required', 'in progress'];

        const interventionToOpen = this.coachingHistory.find(
          (intervention) =>
            statusToOpen.includes(intervention.status.toLowerCase()) &&
            intervention.interventionValue === this.dfService.oneToOneInterventionValue
        );

        if (interventionToOpen) {
          this.dfService.launchSelectedIntervention(interventionToOpen);
        }
      }

      this.data.autoExpandOneToOneForm = false;
    }

    // auto expanding PCC form
    if (this.data.autoExpandPccForm) {
      if (this.coachingHistory.length > 0) {
        const interventionToOpen = this.coachingHistory.find(
          (intervention) =>
            intervention.interventionValue === this.dfService.pccReviewInterventionValue &&
            !intervention.readOnly &&
            intervention.required == 'TRUE' &&
            intervention.complete == 'FALSE'
        );
        if (interventionToOpen) {
          this.dfService.launchSelectedIntervention(interventionToOpen);
        }
      }

      this.data.autoExpandPccForm = false;
    }

    // auto expanding virtual safety discussion
    if (this.data.autoExpandVirtualSafetyDiscussionForm) {
      if (this.coachingHistory.length > 0) {
        const interventionToOpen = this.coachingHistory.find(
          (intervention) =>
            intervention.status.toLowerCase().includes('required') &&
            intervention.interventionValue === this.dfService.virtualSafetyDiscussionInterventionValue
        );

        if (interventionToOpen) {
          this.dfService.launchSelectedIntervention(interventionToOpen);
        }
      }

      this.data.autoExpandVirtualSafetyDiscussionForm = false;
    }

    // auto expanding der
    if (this.data.autoExpandDerForm) {
      if (this.coachingHistory.length > 0) {
        const interventionToOpen = this.coachingHistory.find(
          (intervention) => intervention.submissionId === this.data.targetIntervention
        );

        if (interventionToOpen) {
          this.dfService.launchSelectedIntervention(interventionToOpen);
        }
      }

      this.data.autoExpandDerForm = false;
    }
  }

  getCoachingHistoryTabData(securePin = false) {
    this.dfService.getCoachingHistoryTabDataForkJoined(this.dfService.selectedDriver.pin, securePin).subscribe({
      next: (response) => {
        this.applicableCoachingInterventions = response[0].data;
        this.pccReviewCrashes = response[1].data;

        this.coachingHistoryMeta = response[2];
        this.coachingHistory = response[3].data;

        const canCreateChildIntervention = this.applicableCoachingInterventions.find(
          (intervention) => intervention.allowUserCreation == true
        );
        if (canCreateChildIntervention) {
          this.canCreateAnIntervention = true;
        }

        const checkSingleIntervention =
          this.applicableCoachingInterventions.length == 1
            ? (this.hasSingleIntervention = true)
            : (this.hasSingleIntervention = false);

        if (checkSingleIntervention) {
          this.handleSingleIntervention();
        }

        this.openProvisionedFormByDefault();
        this.updateOutstandingInterventionCount(securePin);
      },
    });
  }

  fetchData(pin: string, securePin = false) {
    this.dialogDataReady = false;

    this.dfService.getEventSummaryForkJoined(pin, securePin).subscribe({
      next: (response) => {
        this.showDetailsBufferProgress = false;

        const activeEventSummaryData = response[0];
        const historicEventSummaryData = response[1];

        this.dfService.selectedDriver.activeEventSummary = activeEventSummaryData;
        this.dfService.selectedDriver.historicEventSummary = historicEventSummaryData;
      },

      error: () => {
        this.showDetailsBufferProgress = false;
      },
    });

    if (this.cService.userProfile && this.cService.userProfile.companyPermissions) {
      if (this.cService.userProfile.companyPermissions.permMisVRMModuleActivity === this.cService.permLevels.TRUE) {
        this.dfService.getVRMModulesSummary(pin, securePin).subscribe({
          next: (response) => {
            this.showDetailsBufferProgress = false;
            this.dfService.selectedDriver.vrmModulesSummary = response;
          },

          error: () => {
            this.showDetailsBufferProgress = false;
          },
        });

        this.dfService.getVRMModulesHistoric(pin, securePin).subscribe({
          next: (response) => {
            this.showDetailsBufferProgress = false;
            this.dfService.selectedDriver.vrmModulesHistoric = response;
          },

          error: () => {
            this.showDetailsBufferProgress = false;
          },
        });
      }

      if (this.cService.userProfile.companyPermissions.permMisExternalTraining === this.cService.permLevels.TRUE) {
        this.dfService.getNonVRMActivity(pin, securePin).subscribe({
          next: (response) => {
            this.showDetailsBufferProgress = false;
            this.dfService.selectedDriver.nonVrmActivity = response;
          },

          error: () => {
            this.showDetailsBufferProgress = false;
          },
        });
      }
    }

    if (
      this.cService.userProfile &&
      this.cService.userProfile.companyPermissions &&
      this.cService.userProfile.companyPermissions.permMisMentorPlaylist === this.cService.permLevels.TRUE
    ) {
      this.dfService.getMentorPlaylistForkJoined(pin, securePin).subscribe({
        next: (response) => {
          this.showDetailsBufferProgress = false;
          const mentorPlaylistSummaryData = response[0];
          const mentorPlaylistModules = response[1];

          this.dfService.selectedDriver.mentorPlaylistSummary = mentorPlaylistSummaryData;
          this.dfService.selectedDriver.mentorPlaylistModules = mentorPlaylistModules;
        },

        error: () => {
          this.showDetailsBufferProgress = false;
        },
      });
    }

    if (
      this.cService.userProfile &&
      this.cService.userProfile.companyPermissions &&
      this.cService.userProfile.companyPermissions.permPointsSystem
    ) {
      this.dfService.getCompanyPointsOverrideOptions().subscribe({
        next: (response) => {
          this.showDetailsBufferProgress = false;
          this.dfService.selectedDriver.companyPointsOverrideOptions = response;
        },

        error: () => {
          this.showDetailsBufferProgress = false;
        },
      });
    }

    // Coach Form - ForkJoined Summaries
    this.dfService.getCoachSummariesWithFusionChartsForkJoined(pin, securePin).subscribe({
      next: (resp) => {
        const chartSummary = resp[0];
        const coachSummary = resp[1];
        const mentorCharts = resp[2];

        this.showDetailsBufferProgress = false;

        console.log('COACH SUMMARY Data Response', coachSummary);

        this.reviewPeriods = coachSummary.data[0].dates;
        this.driverIndexSummaryChart = chartSummary;

        this.mentorCharts = mentorCharts;
        this.renderMultipleCharts = this.mentorCharts.length > 0;
        console.log('Review periods', this.reviewPeriods);

        this.outstandingReviews = coachSummary.data[0].outstandingReviews;
        console.log('Outstanding reviews', this.outstandingReviews);
        setTimeout(() => {
          this.dialogDataReady = true;
        });

        // console.log('Fork Joined Summary Response', resp);
        console.log('Chart Summary', chartSummary);
        console.log('Mentor charts', mentorCharts);
      },
    });

    if (this.canViewMentorRewardsSummary) {
      this.dfService.getMentorRewards(pin, securePin).subscribe({
        next: () => (this.showDetailsBufferProgress = false),
        error: () => (this.showDetailsBufferProgress = false),
      });
    }

    this.getCoachingHistoryTabData(securePin);
    this.getAndInitializeRoadRiskData(securePin);
  }

  submitDynamicForm(event: DynamicFormsEvent) {
    // Prevent auto-save for DriverINDEX Coach
    if (this.dfService.launchedIntervention === this.dfService.driverImprovementInterventionValue) {
      console.log('%c DriverINDEX Coach not allowed to auto-save or save in-complete forms', 'color: lightblue');
    } else {
      console.log('%c On-Save Form Event', 'color: lightblue', event);

      if (event && event.formData) {
        const formName = this.dfService.currentFormStruct.metadata.name;
        const employeePin = this.dfService.selectedDriver.pin;

        delete event.formComplete;
        event.submissionId = this.dfService.currentFormSubmissionId ? this.dfService.currentFormSubmissionId : '';

        this.saveDynamicForm(formName, employeePin, event, () => {
          this.getCoachingHistoryTabData(this.data.securePin);
        });
      }
    }
  }

  private saveDynamicForm(formName: string, employeePin: string, event: any, callback?: () => void | null) {
    this.dfService.saveDynamicForm(formName, employeePin, event).subscribe({
      next: (response: { submissionId: string }) => {
        console.log('%c Saved Dynamic Form RESPONSE', 'color: salmon', response);

        if (!this.dfService.currentFormSubmissionId && response.submissionId) {
          this.dfService.currentFormSubmissionId = response.submissionId;
        }

        this.rptService.formDataUpdated = true;

        if (callback) {
          callback();
        }

        this.cService.showToast(`
          ${this.cService.readyTranslations.form[this.dfService.launchedIntervention]}: ${
          this.cService.readyTranslations.form.formSaved
        }`);
      },

      error: () => {
        this.cService.showToast(`${this.cService.readyTranslations.form[this.dfService.launchedIntervention]}:
            ${this.cService.readyTranslations.form.failedToSaveFormData}`);
      },
    });
  }

  private markFormAsComplete(formEvent: any) {
    const formName = this.dfService.currentFormStruct.metadata.name;
    const employeePin = this.dfService.selectedDriver.pin;

    formEvent.submissionId = this.dfService.currentFormSubmissionId ? this.dfService.currentFormSubmissionId : '';
    formEvent.formComplete = true;

    this.dfService.saveDynamicForm(formName, employeePin, formEvent).subscribe({
      next: (response) => {
        console.log('%c Form Complete RESPONSE', 'color: salmon', response);

        if (response.submissionId) {
          this.dfService.currentFormSubmissionId = response.submissionId;
        }

        this.rptService.formDataUpdated = true;
        this.dfService.formIsReadonly = true;
        this.dfService.currentTabIndex = this.dfService.drilldownTabs.history.id;
        this.getCoachingHistoryTabData(this.data.securePin);
      },

      error: () => {
        this.cService.showToast(`${this.cService.readyTranslations.form[this.dfService.launchedIntervention]}:
          ${this.cService.readyTranslations.form.failedToSaveFormData}`);
      },
    });
  }

  formMayNowBeMarkedAsComplete(event: DynamicFormsCompleteEvent) {
    // NOTE: We only listen for manually triggered completion not auto detected completion
    if (event && event.manuallyTriggered) {
      this.cService.promptConfig = {
        title: event.formValid
          ? this.cService.readyTranslations.form.markSessionAsComplete
          : this.cService.readyTranslations.form.completeAllRequiredFields,

        body: event.formValid
          ? `
        <p>${this.cService.readyTranslations.form.coachingSessionWillNoMoreBeEditable}</p>
        ${this.cService.readyTranslations.form.pressCompleteToConfirm}
        `
          : `
        ${this.cService.readyTranslations.form.coachingCanOnlyBeMarkedCompleteIfRequiredFilled}
        `,
        actionButtonText: event.formValid ? this.cService.readyTranslations.reportData.complete : '',

        cancelButtonText: event.formValid
          ? this.cService.readyTranslations.form.cancel
          : this.cService.readyTranslations.generic.ok,
      };

      this.dialog
        .open(PromptWithActionDialogComponent)
        .afterClosed()
        .subscribe({
          next: (markAsComplete: boolean) => {
            if (markAsComplete && event.formValid) {
              this.markFormAsComplete(event);
            }
          },
        });
    } else {
      // Auto-save has detected that all mandatory fields are now complete.
      // This is only reached assuming the form in use has auto-save enabled.
      console.log('%c Form Complete Triggered by Auto-save', 'color: yellow', event);
    }
  }

  handleSingleIntervention(): void {
    this.singleIntervention = this.applicableCoachingInterventions[0];
    this.isPccReview = this.singleIntervention.value == this.dfService.pccReviewInterventionValue;

    switch (this.singleIntervention.value) {
      case this.dfService.oneToOneInterventionValue:
        this.singleInterventionButtonText = this.cService.readyTranslations.generic.createNewOneToOne;
        break;
      case this.dfService.pccReviewInterventionValue:
        this.singleInterventionButtonText = this.cService.readyTranslations.generic.createNewPccReview;
        break;
      case this.dfService.driverImprovementInterventionValue:
        this.singleInterventionButtonText = this.cService.readyTranslations.generic.createNewDriverIndexCoach;
        break;
      case this.dfService.driverIndexProCoachInterventionValue:
        this.singleInterventionButtonText = this.cService.readyTranslations.generic.createNewDriverIndexProCoach;
        break;
      case this.dfService.virtualSafetyDiscussionInterventionValue:
        this.singleInterventionButtonText = this.cService.readyTranslations.generic.createNewVirtualSafetyDiscussion;
        break;
    }

    /** PccReview is exempt from the below logic */
    if (this.singleIntervention.value !== this.dfService.pccReviewInterventionValue) {
      this.hasPendingSingleIntervention = this.coachingHistory.find((coach) => {
        return (
          coach.interventionValue == this.singleIntervention.value &&
          coach.required.toLowerCase() == 'true' &&
          coach.complete.toLowerCase() == 'false' &&
          coach.status.toLowerCase() != 'closed'
        );
      });
    }
  }
}
